import { useEffect, useMemo, useRef, useState } from 'react';

import { Link } from '@remix-run/react';
import { getTimedLotStatus } from '~/lib/lot-status';
import { formatCurrency, formatDateTime, formatToK, getFormattedDistanceToNow } from '~/lib/utils';
import { optimizedGetProductList } from '~/services/api/products';
import { AuctionType } from '~/types';
import { formatDistanceToNow } from 'date-fns';
import { Clock } from 'lucide-react';

import ResponsiveImage from './responsive-image';

export type Product = NonNullable<Awaited<ReturnType<typeof optimizedGetProductList>>>['list'][number];

export type ProductCardProps = {
  product: Product;
};

export const OptimizedProductCard = ({ product }: ProductCardProps) => {
  const [timeRemaining, setTimeRemaining] = useState('');
  const [lotStatusNow, setLotStatusNow] = useState(() => new Date());
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const [auctionEnded, setAuctionEnded] = useState(() => {
    return !!product.cutOffTime && new Date() >= new Date(product.cutOffTime);
  });

  useEffect(() => {
    const updateRemainingTime = () => {
      const auctionEnd = product.cutOffTime;
      const now = new Date();
      const timeLeft = getFormattedDistanceToNow(auctionEnd);
      setTimeRemaining(timeLeft);
      setLotStatusNow(now);

      if (now >= auctionEnd) {
        setAuctionEnded(true);
        const statusEnd =
          product.auctionType === AuctionType.TIMEONLINE && product.auctionLastCutOff
            ? new Date(product.auctionLastCutOff)
            : auctionEnd;
        if (now >= statusEnd) clearInterval(intervalRef.current as NodeJS.Timeout);
      }
    };

    intervalRef.current = setInterval(updateRemainingTime, 1000);

    return () => clearInterval(intervalRef.current as NodeJS.Timeout);
  }, [product.auctionLastCutOff, product.auctionType, product.cutOffTime]);

  const auctionStarted = useMemo(
    () => (product.auctionStart ? new Date() >= new Date(product.auctionStart) : false),
    [product.auctionStart],
  );
  const timedLotStatus = getTimedLotStatus(
    {
      auctionType: product.auctionType,
      auctionStart: product.auctionStart,
      cutOffTime: product.cutOffTime,
      auctionLastCutOff: product.auctionLastCutOff,
      bidCount: product.bidCount,
      highestBidAmount: product.highestBidAmount,
      lotUnsold: product.lotUnsold,
    },
    lotStatusNow,
  );

  return (
    <Link className="flex flex-col items-start gap-1" to={`/product-detail/${product.slug}`}>
      <div className="relative">
        {timedLotStatus ? (
          timedLotStatus.phase === 'closed' && timedLotStatus.outcome !== null ? (
            <img
              src={`/images/${timedLotStatus.outcome}.png`}
              alt={timedLotStatus.outcome}
              className="absolute -left-8 -top-5 z-10 h-20 w-24"
            />
          ) : null
        ) : !auctionEnded && !product.lotUnsold ? null : !product.lotUnsold && product.bidCount > 0 ? (
          <img src={'/images/sold.png'} alt="sold" className="absolute -left-8 -top-5 z-10 h-20 w-24" />
        ) : (
          <img src={'/images/unsold.png'} alt="unsold" className="absolute -left-8 -top-5 z-10 h-20 w-24" />
        )}
        <ResponsiveImage
          src={product.imageUrl || '/images/placeholder-image-600x600.png'}
          alt={product.name}
          className="mb-2 h-48 w-80 object-cover"
        />
      </div>
      <p className="text-sm font-medium">
        Lot-{product.lotNumber || 'N/A'}: {product.name}
      </p>

      {product.maximumPrice > 0 ? (
        <p className="text-xs font-normal text-gray-500">
          Est: {formatToK(product.minimumPrice)} - {formatToK(product.maximumPrice)}
        </p>
      ) : (
        <p className="text-xs font-normal text-gray-500">Est: {formatToK(product.minimumPrice)}</p>
      )}
      <p className="text-xs font-medium text-black">
        {product.highestBidAmount > 0 ? formatCurrency(product.highestBidAmount) : formatCurrency(product.minimumPrice)}{' '}
        <span className="text-xs font-normal text-slate-500">({product.bidCount} bids)</span>
      </p>
      <div className="flex flex-row items-center gap-1.5">
        <Clock className="h-3.5 w-3.5" color="#64748b" />
        {timedLotStatus ? (
          timedLotStatus.phase === 'scheduled' && product.auctionStart ? (
            <p className="text-xs font-normal text-slate-500">
              Auction starts:{' '}
              <span className="text-red-600">
                {formatDistanceToNow(product.auctionStart, {
                  addSuffix: true,
                  includeSeconds: true,
                })}
              </span>
            </p>
          ) : timedLotStatus.phase === 'closed' ? (
            timedLotStatus.outcome === null ? (
              <p className="text-xs font-normal text-slate-500">Bidding closed</p>
            ) : timedLotStatus.endsAt !== null ? (
              <p className="text-xs font-normal text-slate-500">
                Bidding ended on: {formatDateTime(timedLotStatus.endsAt)}
              </p>
            ) : null
          ) : (
            <p className="text-xs font-normal text-slate-500">
              Bidding ends in: <span className="text-red-600">{timeRemaining}</span>
            </p>
          )
        ) : !auctionStarted && product.auctionStart ? (
          <p className="text-xs font-normal text-slate-500">
            Auction starts:{' '}
            <span className="text-red-600">
              {formatDistanceToNow(product.auctionStart, {
                addSuffix: true,
                includeSeconds: true,
              })}
            </span>
          </p>
        ) : auctionEnded ? (
          product.auctionType === AuctionType.TIME2LIVE ? (
            !product.lotUnsold && product.bidCount > 0 ? (
              <p className="text-xs font-normal text-slate-500">
                Sold on: {formatDateTime(product.cutOffTime)}
              </p>
            ) : null
          ) : (
            <p className="text-xs font-normal text-slate-500">
              Bidding ended on: {formatDateTime(product.cutOffTime)}
            </p>
          )
        ) : product.auctionType === AuctionType.TIME2LIVE && product.liveBiddingMode ? (
          <p className="text-xs font-normal text-red-600">Live now</p>
        ) : product.auctionType === AuctionType.TIME2LIVE ? (
          null
        ) : (
          <p className="text-xs font-normal text-slate-500">
            Bidding ends in: <span className="text-red-600">{timeRemaining}</span>
          </p>
        )}
      </div>
    </Link>
  );
};
